/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */

import images from '@assets/images/index'
import { CellContext, Row } from '@tanstack/react-table'
import { GetCell } from '@utils/assessmentReportUtils'
import { Progress } from 'antd'

import { Database_Procs_TeamBonusGames_Item } from '@suntex.fim/api-client'
import { ColumnType, Dropdown, Tooltip } from '@suntex.fim/ui'

import {
  generateScrollableAssessmentCommonColumns,
  renderWrappedAssessmentHeader
} from './CommonColumns'

export type DataType = {
  loginId: number
  stickers: number
  userId: string
  displayName: string
  isPrimaryTeam: boolean
  equal_weight: number
  equal_length: number
  time_more_or_less: number
  equal_time: number
  passing_time: number
  equal_pay: number
  equal_pay_india: number
  making_change: number
  making_change_india: number
  area_perimeter_square: number
  area_perimeter_circle: number
  area_perimeter_triangle: number
  area_perimeter_rectangle: number
  money_tiles: number
}

/**
 * handles hidable columns list
 */
export const hidableSelectLabel: Record<string, string> = {
  userId: 'User ID',
  displayName: 'Display Name',
  stickers: 'Stickers',
  equal_weight: `Equal Weight`,
  equal_length: 'Equal Length',
  time_more_or_less: `Time More Or Less`,
  equal_time: 'Equal Time',
  passing_time: 'Passing Time',
  equal_pay: 'Equal Pay $',
  equal_pay_india: 'Equal Pay Rupees',
  making_change: 'Making Change',
  making_change_india: 'Making Change Rupees',
  area_perimeter_square: 'Area Square',
  area_perimeter_circle: `Area Circle`,
  area_perimeter_triangle: 'Area Triangle',
  area_perimeter_rectangle: 'Area Rectangle',
  money_tiles: 'Money Tiles'
}

/**
 * returns JSX for measurement world report table
 */
function columns({
  graphColor,
  sendMessage
}: {
  graphColor: string
  sendMessage: (t: Row<Database_Procs_TeamBonusGames_Item>[]) => void
}) {
  const columnKeys = [
    'equal_weight',
    'equal_length',
    'time_more_or_less',
    'equal_time',
    'passing_time',
    'equal_pay',
    'equal_pay_india',
    'making_change',
    'making_change_india',
    'area_perimeter_square',
    'area_perimeter_circle',
    'area_perimeter_triangle',
    'area_perimeter_rectangle',
    'money_tiles'
  ]
  /**
   * renders progress bar in table cell
   */
  const levelColumns: ColumnType<Database_Procs_TeamBonusGames_Item>[] =
    columnKeys.map((key) => ({
      header: () => renderWrappedAssessmentHeader(`${hidableSelectLabel[key]}`),
      accessorKey: key,
      id: key,
      size: 110,
      enableSorting: false,
      cell: (info) => (
        <Tooltip
          title={`${Number(info.getValue()) || 0}% complete`}
          classNames={{ root: 'progress-tooltip' }}
          placement="right"
          arrow={false}
        >
          <Progress
            strokeColor={graphColor}
            percent={Number(info.getValue()) || 0}
            size={['100%', 32]}
            showInfo={false}
          />
        </Tooltip>
      )
    }))
  /**
   * handles table column mapping
   */
  const commonColumns =
    generateScrollableAssessmentCommonColumns<Database_Procs_TeamBonusGames_Item>()

  const finalColumns: ColumnType<Database_Procs_TeamBonusGames_Item>[] = [
    {
      accessorKey: 'action',
      id: 'actions',
      size: 50,
      enableSorting: false,
      header: (header) => {
        return (
          <Dropdown
            ariaLabel="Select visible columns"
            icon={null}
            menu={{
              items: header.table
                .getAllFlatColumns()
                .filter((column) =>
                  Object.keys(hidableSelectLabel).includes(column.id)
                )
                .map((column) => ({
                  key: column.id,
                  label: (
                    <div
                      key={column.id}
                      className="px-1"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <label htmlFor={column.id}>
                        <div className="pretty p-icon p-toggle">
                          <input
                            {...{
                              id: column.id,
                              type: 'checkbox',
                              checked: column.getIsVisible(),
                              onChange: column.getToggleVisibilityHandler()
                            }}
                          />
                          <div className="state p-on">
                            <images.CheckboxCheckedSm />
                          </div>
                          <div className="state p-off">
                            <images.CheckboxSm />
                          </div>
                        </div>{' '}
                        <span>{hidableSelectLabel[column.id]}</span>
                      </label>
                    </div>
                  )
                }))
            }}
            trigger={['click']}
            className="table-header-icon"
            overlayClassName="table-header-icon"
          >
            <div>
              <images.SettingTable />
            </div>
          </Dropdown>
        )
      },
      cell: <T,>(info: CellContext<T, unknown>) => {
        return GetCell(info, sendMessage)
      }
    },
    ...commonColumns,
    ...levelColumns
  ]

  return finalColumns
}

export default columns
